// Состояние каркаса: меню (свёрнуто `[`, поверх экрана до 900 px), меню и окна «Создать ▾»,
// палитра, список клавиш, лента уведомлений. Палитра (T050), клавиши, лента и нижняя
// навигация (T051) открывают слои через это же состояние. Слот меню — место для блока страницы
// под разделами (012: дерево «Структура» протокола, как в прототипе): страница рисует блок
// порталом, пока меню развёрнуто или открыто поверх экрана. Слот крошек — то же для пути в шапке
// листа: страница протокола рисует «Документы / папка / … / протокол» ссылками, крошки маршрута
// на это время не показываются.
//
// Строка документа (ux-dense, решение владельца 25.10.2026: шапка как в Google Таблицах): слот
// действий страницы — справа в шапке листа перед общим поиском; пока он занят, общий поиск
// свёрнут до значка. Режим фокуса — шапка листа и меню приложения скрыты, таблица занимает
// освободившееся место; включает и выключает страница, в адрес состояние не пишется.
//
// 030 (FR-032; ASG-ONB-15): окно «Помощь» — вопрос контролёру. Открывают меню, шапка и нижняя
// навигация сеанса по ссылке, а экран — с пунктом, если вопрос о нём (useHelp).
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode, RefObject } from 'react';
import type { CreateKind } from './permissions.ts';

export interface CreateRequest {
  kind: CreateKind;
  /** Куда вернуть фокус после окна: кнопка «Создать ▾», если окно открыли из её меню. */
  returnFocusRef?: RefObject<HTMLElement | null>;
  /** Номер открытия: каждое открытие — чистая форма. */
  serial: number;
}

/** Окно «Помощь» (030): пункт, если вопрос о нём, и номер открытия — каждое с чистого поля. */
export interface HelpRequest {
  itemId?: string;
  /** «3.2 · П-12» — подпись пункта в окне. */
  itemLabel?: string;
  returnFocusRef?: RefObject<HTMLElement | null>;
  serial: number;
}

export type HelpOptions = Omit<HelpRequest, 'serial'>;

/** Режим палитры (ux.md, раздел 8): ⌘K открывает команды, `/` — сразу поиск. */
export type PaletteMode = 'commands' | 'search';

/** Что получает окно создания из «Создать ▾». */
export interface CreateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnFocusRef?: RefObject<HTMLElement | null>;
}

export interface ShellApi {
  /** Меню свёрнуто до колонки иконок (`[`). */
  menuCollapsed: boolean;
  toggleMenu: () => void;
  /** До 900 px — меню поверх экрана. */
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  /** Палитра ⌘K (T050). */
  paletteOpen: boolean;
  /** С каким режимом палитра открыта: по умолчанию — команды, `/` — поиск (T116). */
  paletteMode: PaletteMode;
  setPaletteOpen: (open: boolean, mode?: PaletteMode) => void;
  /** Список клавиш `?` (T051). */
  shortcutsOpen: boolean;
  setShortcutsOpen: (open: boolean) => void;
  /** Лента уведомлений (T051). */
  notificationsOpen: boolean;
  setNotificationsOpen: (open: boolean) => void;
  /** Меню «Создать ▾» открыто: клавиша N (T051). */
  createMenuOpen: boolean;
  setCreateMenuOpen: (open: boolean) => void;
  /** Окно создания из «Создать ▾», заголовка страницы или палитры. */
  create: CreateRequest | null;
  openCreate: (
    kind: CreateKind,
    options?: { returnFocusRef?: RefObject<HTMLElement | null> },
  ) => void;
  closeCreate: () => void;
  /** Узел под разделами меню для блока страницы; null — меню свёрнуто или закрыто. */
  menuSlot: HTMLElement | null;
  setMenuSlot: (node: HTMLElement | null) => void;
  /** Узел крошек в шапке листа, пока путь рисует страница; иначе null. */
  crumbSlot: HTMLElement | null;
  setCrumbSlot: (node: HTMLElement | null) => void;
  /** Путь рисует страница: крошки маршрута (staticData.crumb) не показываются. */
  pageCrumbs: boolean;
  /** Страница берёт крошки себе; возвращает «отдать обратно». */
  claimCrumbs: () => () => void;
  /** Узел действий страницы в шапке листа, пока их рисует страница; иначе null. */
  actionsSlot: HTMLElement | null;
  setActionsSlot: (node: HTMLElement | null) => void;
  /** Действия рисует страница: общий поиск в шапке — значком. */
  pageActions: boolean;
  /** Страница берёт место действий себе; возвращает «отдать обратно». */
  claimActions: () => () => void;
  /** Режим фокуса: шапка листа и меню приложения скрыты. */
  focusMode: boolean;
  setFocusMode: (on: boolean) => void;
  /** Окно «Помощь» (030); null — закрыто. */
  help: HelpRequest | null;
  openHelp: (options?: HelpOptions) => void;
  closeHelp: () => void;
}

const ShellContext = createContext<ShellApi | null>(null);

export function useShell(): ShellApi {
  const shell = useContext(ShellContext);
  if (!shell) throw new Error('useShell() — только внутри каркаса');
  return shell;
}

/**
 * Слот меню для блока страницы и закрытие меню поверх экрана (переход из блока закрывает меню,
 * как переход по разделу). Вне каркаса слота нет.
 */
export function useMenuSlot(): { slot: HTMLElement | null; closeMenu: () => void } {
  const shell = useContext(ShellContext);
  const setMenuOpen = shell?.setMenuOpen;
  const closeMenu = useCallback(() => setMenuOpen?.(false), [setMenuOpen]);
  return { slot: shell?.menuSlot ?? null, closeMenu };
}

/**
 * Крошки шапки для пути страницы (протокол: «Документы / папка / … / протокол»): пока компонент
 * на экране, крошки маршрута скрыты, а страница рисует путь порталом в возвращённый узел. Вне
 * каркаса узла нет — null.
 */
export function useCrumbSlot(): HTMLElement | null {
  const shell = useContext(ShellContext);
  const claim = shell?.claimCrumbs;
  // До отрисовки: крошки маршрута не мелькают перед путём страницы.
  useLayoutEffect(() => claim?.(), [claim]);
  return shell?.pageCrumbs ? shell.crumbSlot : null;
}

/**
 * Место действий страницы в шапке листа (протокол: «В сети», «Выгрузить ▾», «Зафиксировать
 * неделю», «Во весь экран»): пока компонент на экране, шапка держит узел справа перед общим
 * поиском, а поиск сворачивается до значка. Вне каркаса узла нет — null.
 */
export function useActionsSlot(): HTMLElement | null {
  const shell = useContext(ShellContext);
  const claim = shell?.claimActions;
  useLayoutEffect(() => claim?.(), [claim]);
  return shell?.pageActions ? shell.actionsSlot : null;
}

/** Режим фокуса каркаса; вне каркаса — всегда выключен, переключение ничего не делает. */
export function useFocusMode(): { on: boolean; set: (on: boolean) => void } {
  const shell = useContext(ShellContext);
  const set = shell?.setFocusMode;
  const setSafe = useCallback((on: boolean) => set?.(on), [set]);
  return { on: shell?.focusMode ?? false, set: setSafe };
}

/**
 * «Помощь» с экрана (030, FR-032): открыть окно вопроса контролёру — с пунктом, если вопрос о нём.
 * Вне каркаса окна нет — undefined.
 */
export function useHelp(): ((options?: HelpOptions) => void) | undefined {
  return useContext(ShellContext)?.openHelp;
}

/** Внутри каркаса ли компонент: общий экран (например, «Страница не найдена») не строит второй каркас. */
export function useInShell(): boolean {
  return useContext(ShellContext) !== null;
}

export function ShellProvider({ children }: { children: ReactNode }) {
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpenState] = useState(false);
  const [paletteMode, setPaletteMode] = useState<PaletteMode>('commands');
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [create, setCreate] = useState<CreateRequest | null>(null);
  const [menuSlot, setMenuSlot] = useState<HTMLElement | null>(null);
  const [crumbSlot, setCrumbSlot] = useState<HTMLElement | null>(null);
  const [crumbClaims, setCrumbClaims] = useState(0);
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);
  const [actionClaims, setActionClaims] = useState(0);
  const [focusMode, setFocusMode] = useState(false);
  const [help, setHelp] = useState<HelpRequest | null>(null);
  const serial = useRef(0);

  const toggleMenu = useCallback(() => setMenuCollapsed((value) => !value), []);
  const setPaletteOpen = useCallback<ShellApi['setPaletteOpen']>((open, mode = 'commands') => {
    if (open) setPaletteMode(mode);
    setPaletteOpenState(open);
  }, []);
  const openCreate = useCallback<ShellApi['openCreate']>((kind, options) => {
    serial.current += 1;
    setCreate({ kind, returnFocusRef: options?.returnFocusRef, serial: serial.current });
  }, []);
  const closeCreate = useCallback(() => setCreate(null), []);
  const openHelp = useCallback<ShellApi['openHelp']>((options) => {
    serial.current += 1;
    setHelp({ ...options, serial: serial.current });
  }, []);
  const closeHelp = useCallback(() => setHelp(null), []);
  const claimCrumbs = useCallback(() => {
    setCrumbClaims((count) => count + 1);
    return () => setCrumbClaims((count) => count - 1);
  }, []);
  const pageCrumbs = crumbClaims > 0;
  const claimActions = useCallback(() => {
    setActionClaims((count) => count + 1);
    return () => setActionClaims((count) => count - 1);
  }, []);
  const pageActions = actionClaims > 0;

  const api = useMemo<ShellApi>(
    () => ({
      menuCollapsed,
      toggleMenu,
      menuOpen,
      setMenuOpen,
      paletteOpen,
      paletteMode,
      setPaletteOpen,
      shortcutsOpen,
      setShortcutsOpen,
      notificationsOpen,
      setNotificationsOpen,
      createMenuOpen,
      setCreateMenuOpen,
      create,
      openCreate,
      closeCreate,
      menuSlot,
      setMenuSlot,
      crumbSlot,
      setCrumbSlot,
      pageCrumbs,
      claimCrumbs,
      actionsSlot,
      setActionsSlot,
      pageActions,
      claimActions,
      focusMode,
      setFocusMode,
      help,
      openHelp,
      closeHelp,
    }),
    [
      menuCollapsed,
      toggleMenu,
      menuOpen,
      paletteOpen,
      paletteMode,
      setPaletteOpen,
      shortcutsOpen,
      notificationsOpen,
      createMenuOpen,
      create,
      openCreate,
      closeCreate,
      menuSlot,
      crumbSlot,
      pageCrumbs,
      claimCrumbs,
      actionsSlot,
      pageActions,
      claimActions,
      focusMode,
      help,
      openHelp,
      closeHelp,
    ],
  );

  return <ShellContext.Provider value={api}>{children}</ShellContext.Provider>;
}
